/** Worker movement + behaviour systems for Sprint 2.
 *
 * `MovementSystem` advances every path-following entity along its tile path at a
 * fixed, deterministic rate. `WorkerBrainSystem` is the finite state machine that
 * sends workers home→work→home, requesting A* paths on demand (so they naturally
 * exploit roads). Sprint 3 turns the "work" step into real goods hauling.
 *
 * Scheduler order: MovementSystem then WorkerBrainSystem, so arrival detected by
 * movement is acted on by the brain in the same tick.
 */
import { TICKS_PER_SECOND } from '@settlers/shared';
import type { EntityId } from '@settlers/shared';
import type { System, SimContext } from '../sim/system.js';
import type { World } from '../ecs/world.js';
import { findPath } from '../pathfinding/astar.js';
import {
  CPosition,
  CPathFollower,
  CMovement,
  CWorker,
  CRenderInterp,
  WorkerState,
} from './components.js';

const DT = 1 / TICKS_PER_SECOND;
const WORK_DURATION_TICKS = 30;

const tileXY = (index: number, width: number): { x: number; y: number } => ({
  x: index % width,
  y: Math.floor(index / width),
});

export class MovementSystem implements System {
  readonly name = 'MovementSystem';

  update(ctx: SimContext): void {
    const width = ctx.map.width;
    for (const e of ctx.world.query(CPosition, CPathFollower, CMovement)) {
      const pos = ctx.world.get(e, CPosition);
      const pf = ctx.world.get(e, CPathFollower);
      const mv = ctx.world.get(e, CMovement);
      if (!pos || !pf || !mv) continue;

      // Snapshot last position for render interpolation.
      const interp = ctx.world.get(e, CRenderInterp);
      if (interp) {
        interp.prevX = pos.x;
        interp.prevY = pos.y;
      }

      if (pf.arrived || pf.path.length === 0) continue;

      let budget = mv.speed * DT; // tiles to travel this tick
      while (budget > 0 && !pf.arrived) {
        if (pf.index >= pf.path.length - 1) {
          pf.arrived = true;
          break;
        }
        const next = tileXY(pf.path[pf.index + 1] as number, width);
        const dx = next.x - pos.x;
        const dy = next.y - pos.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= budget) {
          pos.x = next.x;
          pos.y = next.y;
          budget -= dist;
          pf.index++;
          if (pf.index >= pf.path.length - 1) pf.arrived = true;
        } else {
          pos.x += (dx / dist) * budget;
          pos.y += (dy / dist) * budget;
          budget = 0;
        }
      }
    }
  }
}

/** Requests a path from the worker's current tile to `targetTile`. Returns true
 * if the worker is now en route (or already there). */
const routeTo = (ctx: SimContext, e: EntityId, targetTile: number): boolean => {
  const pos = ctx.world.get(e, CPosition);
  const pf = ctx.world.get(e, CPathFollower);
  if (!pos || !pf) return false;
  const sx = Math.round(pos.x);
  const sy = Math.round(pos.y);
  const target = tileXY(targetTile, ctx.map.width);

  if (sx === target.x && sy === target.y) {
    pf.path = [ctx.nav.index(sx, sy)];
    pf.index = 0;
    pf.arrived = true;
    return true;
  }
  const result = findPath(ctx.nav, sx, sy, target.x, target.y);
  if (!result || result.path.length < 2) return false;
  pf.path = result.path;
  pf.index = 0;
  pf.arrived = false;
  return true;
};

export class WorkerBrainSystem implements System {
  readonly name = 'WorkerBrainSystem';

  update(ctx: SimContext): void {
    for (const e of ctx.world.query(CWorker, CPosition, CPathFollower)) {
      const w = ctx.world.get(e, CWorker);
      const pf = ctx.world.get(e, CPathFollower);
      if (!w || !pf) continue;

      switch (w.state) {
        case WorkerState.Idle:
          if (routeTo(ctx, e, w.workTile)) w.state = WorkerState.MovingToWork;
          break;
        case WorkerState.MovingToWork:
          if (pf.arrived) {
            w.state = WorkerState.Working;
            w.workTicksRemaining = WORK_DURATION_TICKS;
          }
          break;
        case WorkerState.Working:
          w.workTicksRemaining--;
          if (w.workTicksRemaining <= 0) {
            if (routeTo(ctx, e, w.homeTile)) w.state = WorkerState.Returning;
          }
          break;
        case WorkerState.Returning:
          if (pf.arrived) {
            w.trips++;
            w.state = WorkerState.Idle;
          }
          break;
      }
    }
  }
}

/** Spawn a worker entity at its home tile, tasked to shuttle to a work tile. */
export const spawnWorker = (
  world: World,
  width: number,
  homeTile: number,
  workTile: number,
  speed = 2.2,
): EntityId => {
  const e = world.create();
  const home = tileXY(homeTile, width);
  world.add(e, CPosition, { x: home.x, y: home.y });
  world.add(e, CRenderInterp, { prevX: home.x, prevY: home.y });
  world.add(e, CMovement, { speed });
  world.add(e, CPathFollower, { path: [], index: 0, arrived: true });
  world.add(e, CWorker, {
    state: WorkerState.Idle,
    homeTile,
    workTile,
    workTicksRemaining: 0,
    trips: 0,
  });
  return e;
};
